/**
 * Client-side route guard for the deck editor.
 *
 * The server enforces ``_require_flexcompute_employee`` on every editor
 * route, so this guard is *defence in depth* — it prevents non-Flex users
 * from loading the editor UI (which would only show network errors) and
 * shows a clear "you can't use this" page instead.
 *
 * Auth happens earlier in ``App.tsx`` (Keycloak via Supabase-compatible
 * shim). If the user is not signed in at all, we never reach this guard.
 */

import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { authClient } from "../../../lib/auth";

interface FlexcomputeOnlyGuardProps {
  children: ReactNode;
  /** Lets tests inject a fixed email. Production reads from the auth session. */
  testEmail?: string | null;
  /** Heading on the blocked page. Defaults to the generic employees-only line. */
  title?: ReactNode;
  /**
   * Body copy on the blocked page, so each guarded surface can say what it is.
   * Defaults to the deck-editor wording the guard shipped with.
   */
  description?: ReactNode;
}

const DEFAULT_BLOCKED_TITLE = "Flexcompute employees only";
const DEFAULT_BLOCKED_DESCRIPTION =
  "The deck editor is internal Flexcompute tooling. Sign in with your " +
  "@flexcompute.com account to continue, or share a customer preview link if " +
  "you need to show a deck externally.";

// Local dev (vite dev server, or VITE_THREAD_ENV=local): the app bypasses auth
// (App.tsx) and the API uses a dev stub, so the gate would otherwise block local
// development. Bypass it here too — a production build (import.meta.env.DEV === false)
// still enforces it, as does the backend on real tokens. Tests inject `testEmail`
// to exercise the real gate, so that path is always honoured.
const LOCAL_DEV_BYPASS =
  import.meta.env.DEV || import.meta.env.VITE_THREAD_ENV === "local";

export function FlexcomputeOnlyGuard(props: FlexcomputeOnlyGuardProps) {
  if (LOCAL_DEV_BYPASS && props.testEmail === undefined) {
    return <>{props.children}</>;
  }
  return <FlexcomputeGuardInner {...props} />;
}

function FlexcomputeGuardInner({
  children,
  testEmail,
  title = DEFAULT_BLOCKED_TITLE,
  description = DEFAULT_BLOCKED_DESCRIPTION,
}: FlexcomputeOnlyGuardProps) {
  const [email, setEmail] = useState<string | null | undefined>(testEmail);
  const [resolved, setResolved] = useState(testEmail !== undefined);

  useEffect(() => {
    if (testEmail !== undefined) {
      setEmail(testEmail);
      setResolved(true);
      return;
    }
    let mounted = true;
    void authClient.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        setEmail(session?.user?.email ?? null);
        setResolved(true);
      })
      .catch(() => {
        if (!mounted) return;
        setEmail(null);
        setResolved(true);
      });
    return () => {
      mounted = false;
    };
  }, [testEmail]);

  if (!resolved) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          color: "var(--gray-9)",
        }}
        data-testid="flexcompute-guard-loading"
      >
        Checking access…
      </div>
    );
  }

  const isFlex =
    typeof email === "string" && email.toLowerCase().endsWith("@flexcompute.com");

  if (!isFlex) {
    return (
      <div
        style={{
          maxWidth: 480,
          margin: "10vh auto",
          padding: "1.5rem",
          textAlign: "center",
          color: "var(--gray-12)",
        }}
        data-testid="flexcompute-guard-blocked"
      >
        <h1 style={{ marginBottom: "0.5rem" }}>{title}</h1>
        <p style={{ color: "var(--gray-10)" }}>{description}</p>
      </div>
    );
  }

  return <>{children}</>;
}
